import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  RANK_LABEL,
  api,
  canManageUsers,
  type AuthUser,
  type Rank,
  type UserRow,
} from '@/lib/api';

const RANKS: Rank[] = ['R5', 'R4', 'R3', 'R2', 'R1'];

export default function Users({ user }: { user: AuthUser }) {
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: api.listUsers });
  const playersQuery = useQuery({
    queryKey: ['players', { includeInactive: true }],
    queryFn: () => api.listPlayers({ includeInactive: true }),
  });

  if (!canManageUsers(user.role)) {
    return <div className="text-slate-400">Only R5 can manage users.</div>;
  }

  const playerName = (id: number | null) =>
    id ? playersQuery.data?.find((p) => p.id === id)?.name ?? `#${id}` : '—';

  return (
    <div className="space-y-6">
      <CreateUserForm players={playersQuery.data ?? []} />
      <section className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2">Username</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Linked player</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Last login</th>
              <th className="px-3 py-2">Last seen</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {usersQuery.isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {usersQuery.data?.map((u) => (
              <UserRowEditor
                key={u.id}
                row={u}
                selfId={user.id}
                playerName={playerName(u.playerId)}
                players={playersQuery.data ?? []}
              />
            ))}
          </tbody>
        </table>
      </section>
      <Style />
    </div>
  );
}

function fmtRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.round((now - t) / 1000));
  if (diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 30 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function CreateUserForm({ players }: { players: { id: number; name: string }[] }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Rank>('R3');
  const [playerId, setPlayerId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      api.createUser({
        username,
        password,
        role,
        playerId: playerId ? Number(playerId) : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setUsername('');
      setPassword('');
      setPlayerId('');
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed'),
  });

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
        Create user
      </h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="grid grid-cols-1 gap-3 md:grid-cols-5"
      >
        <input
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          className="input"
        />
        <input
          type="password"
          placeholder="Password (min 8)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          className="input"
        />
        <select value={role} onChange={(e) => setRole(e.target.value as Rank)} className="input">
          {RANKS.map((r) => (
            <option key={r} value={r}>
              {RANK_LABEL[r]}
            </option>
          ))}
        </select>
        <select
          value={playerId}
          onChange={(e) => setPlayerId(e.target.value)}
          className="input"
        >
          <option value="">— No player link —</option>
          {players.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <button className="btn-primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Creating…' : 'Create'}
        </button>
      </form>
      {error && <div className="text-sm text-red-400">{error}</div>}
    </section>
  );
}

function UserRowEditor({
  row,
  selfId,
  playerName,
  players,
}: {
  row: UserRow;
  selfId: number;
  playerName: string;
  players: { id: number; name: string }[];
}) {
  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState(row.username);
  const [role, setRole] = useState<Rank>(row.role);
  const [playerId, setPlayerId] = useState<string>(row.playerId?.toString() ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const isSelf = row.id === selfId;

  const save = useMutation({
    mutationFn: () =>
      api.updateUser(row.id, {
        username: username !== row.username ? username : undefined,
        role,
        playerId: playerId ? Number(playerId) : null,
        password: password || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setEditing(false);
      setPassword('');
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  const toggleActive = useMutation({
    mutationFn: () => api.updateUser(row.id, { active: !row.active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed'),
  });

  const rowClass = row.active ? '' : 'opacity-60';

  if (!editing) {
    return (
      <tr className={rowClass}>
        <td className="px-3 py-2 font-medium">
          {row.username}
          {isSelf && <span className="ml-2 text-xs text-slate-500">(you)</span>}
        </td>
        <td className="px-3 py-2 text-slate-300">{RANK_LABEL[row.role]}</td>
        <td className="px-3 py-2 text-slate-400">{playerName}</td>
        <td className="px-3 py-2">
          <span
            className={
              row.active
                ? 'inline-flex rounded bg-emerald-900/40 px-2 py-0.5 text-xs text-emerald-300'
                : 'inline-flex rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-400'
            }
          >
            {row.active ? 'Active' : 'Deactivated'}
          </span>
        </td>
        <td
          className="px-3 py-2 text-slate-300"
          title={row.lastLoginAt ?? undefined}
        >
          {fmtRelative(row.lastLoginAt)}
        </td>
        <td
          className="px-3 py-2 text-slate-300"
          title={row.lastSeenAt ?? undefined}
        >
          {fmtRelative(row.lastSeenAt)}
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(true)}
              className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
            >
              Edit
            </button>
            <button
              onClick={() => toggleActive.mutate()}
              disabled={toggleActive.isPending || isSelf}
              title={
                isSelf ? 'You cannot deactivate your own account' : undefined
              }
              className={
                row.active
                  ? 'rounded border border-amber-800 px-2 py-1 text-xs text-amber-300 hover:bg-amber-900/40 disabled:cursor-not-allowed disabled:opacity-40'
                  : 'rounded border border-emerald-800 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-900/40 disabled:opacity-40'
              }
            >
              {row.active ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
          {error && !editing && (
            <div className="mt-1 text-right text-xs text-red-400">{error}</div>
          )}
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-slate-900/40">
      <td className="px-3 py-2">
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="input"
        />
      </td>
      <td className="px-3 py-2">
        <select value={role} onChange={(e) => setRole(e.target.value as Rank)} className="input">
          {RANKS.map((r) => (
            <option key={r} value={r}>
              {RANK_LABEL[r]}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2">
        <select
          value={playerId}
          onChange={(e) => setPlayerId(e.target.value)}
          className="input"
        >
          <option value="">— No player link —</option>
          {players.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-slate-500" colSpan={3}>
        <input
          type="password"
          placeholder="New password (optional)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input"
        />
        {error && <div className="mt-1 text-xs text-red-400">{error}</div>}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="btn-primary"
          >
            Save
          </button>
          <button
            onClick={() => {
              setEditing(false);
              setUsername(row.username);
              setRole(row.role);
              setPlayerId(row.playerId?.toString() ?? '');
              setPassword('');
              setError(null);
            }}
            className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
        width: 100%;
      }
      .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
      .btn-primary {
        background: rgb(79 70 229 / 1);
        color: white;
        padding: 6px 12px;
        font-size: 14px;
        border-radius: 6px;
      }
      .btn-primary:hover { background: rgb(99 102 241 / 1); }
      .btn-primary:disabled { opacity: 0.5; }
    `}</style>
  );
}
